import { NotificationTypes, type SafeAny } from '@/common/types';
import type { EpochParams } from '@/modules/epoch/types';
import { getStudyData } from '@/modules/study/data-access';
import {
  clearSignalCaches,
  loadEpochData,
  preloadAdjacentEpochSizeEpochs,
  preloadAdjacentEpochs
} from './signalHelpers';
import { isCancel } from 'axios';
import { EPOCH_SIZES } from '@/common/utils';

export const getSignalData = async (
  store: SafeAny,
  payload: EpochParams & { foregroundSignal: AbortSignal; backgroundSignal: AbortSignal }
) => {
  const { rootState, state, commit } = store;
  const isBackgroundLoadingRef = { value: false };
  try {
    state.epochData.requestedEpoch = payload.epochSelected;

    state.epochData.isLoading = true;
    state.epochData.error = undefined;
    // NOTE: do NOT clear state.epochData.data here. Wiping it unconditionally
    // before the new epoch resolves causes any consumer that falls back to
    // `state.epochData.data` (e.g. binary-ref entries in epoch.view.vue's
    // `fileData` computed) to render blank for the duration of the fetch —
    // which is what the "signals disappear" reports describe. The fresh
    // payload is assigned below at line ~114; the configurationChanged branch
    // further down handles the actual config-change reset.

    const cachedSetsForEpochSize = !!rootState.study.config.data?.sets?.[payload.epochSize];
    const baseEpochSize = Math.min(...EPOCH_SIZES.map((size) => size.inSeconds));
    const cachedBaseSets = !!rootState.study.config.data?.sets?.[baseEpochSize];
    const hasConfig = !!rootState.study.config.data?.config;

    const shouldFetchStudyData =
      payload.id !== rootState.study.config.data?.id ||
      payload.notch !== rootState.study.config.data?.notch ||
      !hasConfig ||
      (!cachedSetsForEpochSize && !cachedBaseSets);

    if (shouldFetchStudyData) {
      await getStudyData(store, payload);
    }

    const fgSignal = payload.foregroundSignal;
    const bgSignal = payload.backgroundSignal;

    const { selectedStudy, selectedNotch, selectedEpochSize } = state.epochData;
    const { id, notch } = payload;

    const oldConfigKey =
      selectedStudy && selectedNotch ? `${selectedStudy}|${selectedNotch}` : null;
    const newConfigKey = `${id}|${notch}`;

    if (!state.epochData.sets) state.epochData.sets = {};

    const configurationChanged = !!oldConfigKey && oldConfigKey !== newConfigKey;
    const epochSizeChanged =
      Number.isFinite(selectedEpochSize) && selectedEpochSize !== payload.epochSize;

    if (configurationChanged) {
      state.epochData.sets = {};
      state.epochData.cacheByConfig = {};
      clearSignalCaches({ clearBinary: true });
      state.epochData.isLoading = true;
      state.epochData.error = undefined;
    } else if (epochSizeChanged) {
      Object.keys(state.epochData.sets).forEach((epochSize) => {
        const size = Number(epochSize);
        if (!Number.isNaN(size) && size !== baseEpochSize) {
          delete state.epochData.sets[size];
        }
      });
    }
    let loaded = await loadEpochData(
      payload.epochSelected,
      payload.epochSize,
      state,
      rootState,
      fgSignal,
      commit
    );

    // Safety net: if loadEpochData returned undefined but our own signal is still alive,
    // it most likely reused a background in-flight promise that resolved to undefined
    // due to an aborted bgSignal. Retry once with a fresh call now that the stale
    // in-flight entry has been removed from the deduplication map.
    if (loaded === undefined && !fgSignal.aborted) {
      loaded = await loadEpochData(
        payload.epochSelected,
        payload.epochSize,
        state,
        rootState,
        fgSignal,
        commit
      );
    }

    const cached = state.epochData?.sets?.[payload.epochSize]?.[payload.epochSelected];
    const isBinaryRef = cached && typeof cached === 'object' && cached.__binaryRef;
    const epochPayload = isBinaryRef ? loaded : (cached ?? loaded);

    if (!epochPayload) {
      // Ensure isLoading is cleared even on empty result — prevents the loading state
      // from getting permanently stuck when a navigation produces no data.
      state.epochData.isLoading = false;
      return;
    }

    if (state.epochData.requestedEpoch !== payload.epochSelected) {
      state.epochData.isLoading = false;
      return;
    }

    state.epochData.data = epochPayload;
    state.epochData.displayedEpoch = payload.epochSelected;

    setDummySignal(state); // This will be deleted later after finding a proper place for sleep stages.

    //update signals list right when we have epochdata updated
    commit('setSignalSettingsListFromAPI');
    state.epochData.isLoading = false;

    state.epochData.selectedStudy = payload.id;
    state.epochData.selectedNotch = payload.notch;
    state.epochData.selectedEpochSize = payload.epochSize;

    const currentStudyEpochCount = rootState.study.config.data.sets?.[payload.epochSize]?.length;
    const currentEpochSetCount = state.epochData?.sets?.[payload.epochSize]
      ? Object.keys(state.epochData.sets?.[payload.epochSize]).length
      : 0;

    if (
      currentStudyEpochCount &&
      currentEpochSetCount &&
      currentStudyEpochCount > currentEpochSetCount
    ) {
      // current set is not complete, preload adjacent epochs
      preloadAdjacentEpochs(
        payload.epochSelected,
        payload.epochSize,
        store,
        bgSignal,
        isBackgroundLoadingRef
      );
    }
    if (
      currentStudyEpochCount &&
      currentEpochSetCount &&
      currentStudyEpochCount === currentEpochSetCount
    ) {
      preloadAdjacentEpochSizeEpochs(payload.epochSize, store, bgSignal, isBackgroundLoadingRef);
    }
  } catch (error: SafeAny) {
    state.epochData.isLoading = false;
    state.epochData.error = error;
    state.epochData.failedEpoch = payload.epochSelected;

    if (isCancel(error)) {
      return;
    }

    commit(
      'addNotification',
      {
        i18nTitle: 'errorGetSignalData',
        i18nDescription: error.message,
        type: NotificationTypes.Error
      },
      { root: true }
    );
  }
};

const setDummySignal = (state) => {
  if (state.epochData?.data?.mapping) {
    state.epochData.data.mapping['Sleep_Stages'] = {
      min: -1,
      max: 1,
      defaultOffset: 0,
      defaultScale: 0,
      unit: 'uV',
      vizMode: 1
    };
  }
  if (state.epochData?.data?.signal) {
    state.epochData.data.signal['Sleep_Stages'] = [[{ 0: {} }]];
  }
};
